import * as React from "react";
import * as _ from "lodash";
import {FieldSet, ValidationResult} from "./Form";
import {Validator, Validators} from "./Validators";
import * as Codemirror from "react-codemirror";
import {FormTransferContainer} from "./FormTransferContainer";
import * as Big from "big.js";

export interface FormElement<M, S extends HTMLElement> extends React.HTMLProps<S> {
    modelValue?: M;
    valid?: boolean;
    fieldSet?: FieldSet;
    model?: string;
    validators?: Array<Validator<any>>;
    errorClass?: string;
    onChange?: () => void;
}

var requiredValidator = Validators.required();


export abstract class AbstractFormElement<M, E extends FormElement<M, HTMLElement>, T> extends React.Component<E, T> {
    constructor(props: E) {
        super(props);
    }

    componentDidMount() {
        var modelValue: any = this.props.modelValue;
        if (modelValue == null || modelValue == "undefined") {
            modelValue = this.getDefaultValue();
        }
        this.props.fieldSet.setInitialModel(this.props.model, modelValue, true, this.validate(modelValue, true))
    }

    getDefaultValue(): M {
        return null;
    }


    abstract validate(modelValue: M, pristine: boolean): ValidationResult ;

    getModelObject() {
        return this.props.fieldSet.getModelObject();
    }

    updateModelValue(value, pristine) {
        var result = this.validate(value, pristine);
        this.props.fieldSet.updateModel(this.props.model, value, pristine, result);
    }

    getModelValue() {
        var modelValue: any = this.props.modelValue;
        if (modelValue == null || modelValue == "undefined") {
            modelValue = this.getDefaultValue();
        }
        return modelValue;
    }

    renderFormElements() {

    }

    render() {
        return (
            <FormTransferContainer  {...this.props as any}>
                {this.renderFormElements()}
            </FormTransferContainer>
        );
    }
}


export abstract class AbstractInputElement<M, E extends FormElement<M, HTMLElement>, T> extends AbstractFormElement<M, E, T> {


    constructor(props: E) {
        super(props);
        if (props.model == "undefined" || props.model == null || props.model == "") {
            console.warn("Model was not set on form element", props, new Error());
        }

    }

    componentWillUnmount() {
        this.props.fieldSet.removeValidation(this.props.model);
    }

    getClassNames() {
        var className = this.props.className
        if (this.isValid() == false) {
            var errorClass = this.props.errorClass;
            className = className + " " + (errorClass != null ? errorClass : "error");
        }
        if (process.env.NODE_ENV !== "production" && this.props.model) {
            className += " fm-debug-" + (this.props.model.replace(".", "_"));
        }
        return className;
    }

    shouldComponentUpdate(nextProps: E, nextState) {
        if (!_.isEqual(nextProps.modelValue, this.props.modelValue) || nextProps.valid != this.props.valid || nextProps.disabled != this.props.disabled) {
            return true;
        } else if (!_.isEqual(nextProps.validators, this.props.validators)) {
            return true;
        } else {
            return false;
        }
    }

    validate(value: any, pristine: boolean): ValidationResult {
        if (this.props.required == true) {
            if (!requiredValidator.validate(value, this.getModelObject())) {
                return Validators.error(requiredValidator.getName(), pristine);
            }
        }

        var validators: Validator<any>[] = this.getValidators();

        if (typeof validators == "undefined" || validators == null || validators.length == 0) {
            return Validators.valid(pristine);
        }
        for (var i = 0; i < validators.length; i++) {
            if (!validators[i].validate(value, this.getModelObject())) {
                return Validators.error(validators[i].getName(), pristine);
            }
        }
        return Validators.valid(pristine);
    }

    getValidators(): Validator<any>[] {
        return (this.props.validators as Validator<any>[]);
    }

    isValid() {
        return this.props.fieldSet.isModelValid(this.props.model);
    }

    handleChange(event: React.FormEvent) {
        var value: M = (event.target as any).value;
        this.onChange(value);

    }

    onChange(value: M) {
        this.updateModelValue(value, false);
    }

}

export class FormInput extends AbstractInputElement<string | number | boolean, FormElement<string, HTMLInputElement>, {}> {

    handleChange(event: React.FormEvent) {
        var value: string | number;
        if (this.props.type == "checkbox") {
            value = (event.target as any).checked;
        } else {
            value = (event.target as any).value;
        }

        if (this.props.type == "integer") {
            if (!isNaN(parseInt("" + value))) {
                value = parseInt("" + value);
            }
        } else if (this.props.type == "float") {
            if (!("" + value).endsWith(".") && !isNaN(parseFloat("" + value))) {
                value = parseFloat("" + value);
            }
        }
        this.onChange(value);
        if(this.props.onChange) this.props.onChange();
    }

    getDefaultValue(): string | number | boolean {
        let type = this.props.type;
        if (type == "checkbox") {
            return !!this.props.checked
        } else if (type == "integer") {
            return null;
        } else if (type == "float") {
            return null;
        } else {
            return "";
        }
    }

    render() {
        let rest = _.omit(this.props, ['validators', `model`, `fieldSet`, `modelValue`, `valid`, 'validationMap']);
        if (this.props.type == "checkbox") {
            let modelValue: boolean = "" + this.props.modelValue == "true";
            return <input {...rest} className={this.getClassNames()} checked={modelValue}
                          onChange={this.handleChange.bind(this)}/>
        } else {
            let type = this.props.type;
            if (type === "integer") {
                type = "text";
            } else if (type === "float") {
                type = "text";
            }
            return <input {...rest} type={type} required={null} className={this.getClassNames()}
                          value={this.getModelValue() == null ? "" : this.getModelValue()}
                          onChange={this.handleChange.bind(this)}/>
        }

    }

}

/**
 * Input for displaying fractions (0.1) as percentage (10)
 */
export class FractionInput extends AbstractInputElement<string, FormElement<string, HTMLInputElement>, {}> {

    onChange(value: string) {
        if (!isNaN(parseFloat(value))) {
            this.updateModelValue(new Big(value).div(100).toString(), false);
        } else {
            return this.updateModelValue(value, false);
        }
    }

    getValue() {
        var value = (this.getModelValue() as string);
        if (!isNaN(parseFloat(value))) {
            return new Big(value).times(100).toString();
        } else {
            return value;
        }
    }

    render() {
        let rest = _.omit(this.props, ['validators', `model`, `fieldSet`, `modelValue`, `valid`, 'validationMap']);
        return <input {...rest} required={null} type="text" className={this.getClassNames()} value={this.getValue()}
                      onChange={this.handleChange.bind(this)}/>

    }

}


export class FormJavascript extends AbstractInputElement<string, FormElement<string, HTMLElement>, {}> {

    codeMirrorOptions = {
        mode: "javascript",
        lineNumbers: true,
        theme: "dracula"
    }

    codeChange(e) {
        super.onChange(e);
    }

    render() {
        return <Codemirror options={this.codeMirrorOptions} onChange={this.codeChange.bind(this)}
                           value={this.getModelValue()}/>;
    }

}







// WEBPACK FOOTER //
// ./src/app/ui/form/FormInput.tsx